import { AppConfig } from './../app.config';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { CognitoCallback, CognitoUtil, LoggedInCallback } from './cognito.service';
import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserSession,
    ICognitoUserSessionData,
    CognitoAccessToken,
    CognitoIdToken,
    CognitoRefreshToken,
    ICognitoUserData
} from 'amazon-cognito-identity-js';
import { ServiceRegistry } from '../service/service-registry.service';

import * as AWS from 'aws-sdk';
import * as STS from 'aws-sdk/clients/sts';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';

@Injectable()
export class UserLoginService {

    constructor(public cognitoUtil: CognitoUtil,
        private serviceRegistry: ServiceRegistry,
        private appConfig: AppConfig,
        private http: HttpClient,
        private router: Router,
        @Inject(DOCUMENT) private document: any) {
    }

    authenticate(username: string, password: string, callback: CognitoCallback) {
        console.log('UserLoginService: starting the authentication');

        const authenticationData = {
            Username: username,
            Password: password,
        };
        const authenticationDetails = new AuthenticationDetails(authenticationData);

        const userData = {
            Username: username,
            Pool: this.cognitoUtil.getUserPool()
        };

        console.log('UserLoginService: Params set...Authenticating the user');
        const cognitoUser = new CognitoUser(userData);
        console.log('UserLoginService: config is ' + AWS.config);
        const self = this;
        cognitoUser.authenticateUser(authenticationDetails, {
            newPasswordRequired: function (userAttributes, requiredAttributes) {
                callback.cognitoCallback(`User needs to set password.`, null);
            },
            onSuccess: function (result) {
                console.log('In authenticateUser onSuccess callback');
                const creds = self.cognitoUtil.buildCognitoCreds(result.getAccessToken().getJwtToken());
                AWS.config.credentials = creds;
                // So, when CognitoIdentity authenticates a user, it doesn't actually hand us the IdentityID,
                // used by many of our other handlers. This is handled by some sly underhanded calls to AWS Cognito
                // API's by the SDK itself, automatically when the first AWS SDK request is made that requires our
                // security credentials. The identity is then injected directly into the credentials object.
                // If the first SDK call we make wants to use our IdentityID, we have a
                // chicken and egg problem on our hands. We resolve this problem by "priming" the AWS SDK by calling a
                // very innocuous API call that forces this behavior.
                const sts = new STS();
                sts.getCallerIdentity(function (err, data) {
                    console.log('UserLoginService: Successfully set the AWS credentials');
                    callback.cognitoCallback(null, result);
                });

            },
            onFailure: function (err) {
                callback.cognitoCallback(err.message, null);
            },
        });
    }

    forgotPassword(username: string, callback: CognitoCallback) {
        const userData = {
            Username: username,
            Pool: this.cognitoUtil.getUserPool()
        };
        const cognitoUser = new CognitoUser(userData);

        cognitoUser.forgotPassword({
            onSuccess: function () {

            },
            onFailure: function (err) {
                callback.cognitoCallback(err.message, null);
            },
            inputVerificationCode() {
                callback.cognitoCallback(null, null);
            }
        });
    }

    confirmNewPassword(email: string, verificationCode: string, password: string, callback: CognitoCallback) {
        const userData = {
            Username: email,
            Pool: this.cognitoUtil.getUserPool()
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmPassword(verificationCode, password, {
            onSuccess: function () {
                callback.cognitoCallback(null, null);
            },
            onFailure: function (err) {
                callback.cognitoCallback(err.message, null);
            }
        });
    }

    async getCurrentUserWithSession(): Promise<CognitoUser> {
        return new Promise((resolve, reject) => {
            const currentUser = this.cognitoUtil.getCurrentUser();
            currentUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(currentUser);
                }
            });
        });
    }

    async globalLogout(): Promise<string> {
        // Need to call this so globalSignout can find a session
        const currentUser = await this.getCurrentUserWithSession();
        return new Promise((resolve, reject) => {
            currentUser.globalSignOut({
                onSuccess: (msg: string) => {
                    console.log(`UserLoginService: Globally signed out: ${msg}`);
                    resolve(msg)
                },
                onFailure: (err: Error) => {
                    reject(err);
                }
            });
        });
    }

    async logoutAsync(): Promise<void> {
        // Stop the polling services
        this.serviceRegistry.pausePollingServices();
        console.log('UserLoginService: Logging out');
        const clientId = this.cognitoUtil.getUserPool().getClientId();
        const currentUser = this.cognitoUtil.getCurrentUser();
        const refreshToken = await this.cognitoUtil.getRefreshToken();
        await this.globalLogout();
        await new Promise((resolve, reject) => {
            currentUser.signOut(() => {
                console.log('Signed out');
                resolve()
            });
        });
        // const cisp = new AWS.CognitoIdentityServiceProvider();
        // await cisp.revokeToken({
        //     Token: refreshToken.getToken(),
        //     ClientId: clientId
        // } as AWS.CognitoIdentityServiceProvider.Types.RevokeTokenRequest).promise();

        if (AWS.config.credentials && AWS.config.credentials instanceof AWS.CognitoIdentityCredentials) {
            const cognitoCredentials: AWS.CognitoIdentityCredentials = AWS.config.credentials;
            cognitoCredentials.clearCachedId();
        }
        const lastUserKey = 'CognitoIdentityServiceProvider.' + clientId + '.LastAuthUser';
        window.localStorage.removeItem(lastUserKey);
        window.sessionStorage.removeItem(lastUserKey);
        window.sessionStorage.removeItem('crossSessionGlobalLogoutPerformed');
    }

    // Deprecated
    logout(callback: (message?: string, err?: Error) => void) {
        // Stop the polling services
        this.serviceRegistry.pausePollingServices();

        console.log('UserLoginService: Logging out');
        const cb = {
            onSuccess: (successMessage: string) => {
                callback(`UserLoginService: Globally signed out: ${successMessage}`, undefined);
            },
            onFailure: (err: Error) => {
                console.log(err);
                callback(undefined, err);
            }
        };
        const clientId = this.cognitoUtil.getUserPool().getClientId();
        const currentUser = this.cognitoUtil.getCurrentUser();

        currentUser.getSession((err, session) => {
            // Need to call this so globalSignout can find a session
            if (err) {
                callback(`Failed to get session for signout: ${err}`);
            } else {
                currentUser.globalSignOut(cb);
            }
        });
        currentUser.signOut(() => {
            console.log('Signed out');
        });
        if (AWS.config.credentials && AWS.config.credentials instanceof AWS.CognitoIdentityCredentials) {
            const cognitoCredentials: AWS.CognitoIdentityCredentials = AWS.config.credentials;
            cognitoCredentials.clearCachedId();
        }
        const lastUserKey = 'CognitoIdentityServiceProvider.' + clientId + '.LastAuthUser';
        window.localStorage.removeItem(lastUserKey);
        window.sessionStorage.removeItem(lastUserKey);
    }

    isAuthenticated(callback: LoggedInCallback) {
        if (this.appConfig.getUseJSONData()) {
            console.log('=== Using local demo data ===');
            callback.isLoggedIn(undefined, true);
            return;
        }
        if (callback == null) {
            throw new Error('UserLoginService: Callback in isAuthenticated() cannot be null');
        }

        const cognitoUser = this.cognitoUtil.getCurrentUser();

        if (cognitoUser != null) {
            cognitoUser.getSession(function (err, session) {
                if (err) {
                    console.log('UserLoginService: Couldn\'t get the session: ' + err, err.stack);
                    callback.isLoggedIn(err, false);
                } else {
                    console.log('UserLoginService: Session is ' + session.isValid());
                    callback.isLoggedIn(err, session.isValid());
                }
            });
        } else {
            console.log('UserLoginService: can\'t retrieve the current user');
            callback.isLoggedIn('Can\'t retrieve the CurrentUser', false);
        }
    }


    doCognitoAuthLogin() {
        const regex = /\?code\=(.*)/;
        const match = regex.exec(window.location.href);
        const tenantInfo = this.appConfig.getTenantInfo();
        const cognitoDomainPrefix = tenantInfo.cognitoDomainPrefix;
        // const redirectUrl = 'https://localhost:4200';
        // const redirectUrl = 'https://development.liquitics.com';
        let redirectUrl = String(this.document.location.href);
        const codeIndex = redirectUrl.indexOf('?code=');
        if (codeIndex !== -1) {
            redirectUrl = redirectUrl.substring(0, codeIndex);
        }
        if (redirectUrl.endsWith('/#/')) {
            redirectUrl = redirectUrl.substring(0, redirectUrl.length - 3);
        } else if (redirectUrl.endsWith('/#')) {
            redirectUrl = redirectUrl.substring(0, redirectUrl.length - 2);
        } else if (redirectUrl.endsWith('/')) {
            redirectUrl = redirectUrl.substring(0, redirectUrl.length - 1);
        }
        const pool = this.cognitoUtil.getUserPool();
        if (!match || match.length <= 1) {
            // Didn't get forwarded from SAML redirect

            // tslint:disable-next-line:max-line-length
            const loginUrl = `https://${cognitoDomainPrefix}.auth.${tenantInfo.region}.amazoncognito.com/login?response_type=code&client_id=${pool.getClientId()}&&redirect_uri=${redirectUrl}`;
            this.document.location.href = loginUrl;
            // this.router.navigate(['/login']);
            return;
        }
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded')
            ;
        // .set('Content-Type', 'application/json')
        const options = { headers: headers };
        let code = match[1];
        code = code.replace('#/', '');
        const body =
            'grant_type=authorization_code&' +
            'client_id=' + this.cognitoUtil.getUserPool().getClientId() + '&' +
            'code=' + code + '&' +
            'redirect_uri=' + redirectUrl;
        const tokenUrl = `https://${cognitoDomainPrefix}.auth.${tenantInfo.region}.amazoncognito.com/oauth2/token`;
        this.http.post<any>(tokenUrl, body, options)
            .toPromise()
            .then((tokens: any) => {
                const cognito = new AWS.CognitoIdentityServiceProvider();
                const getUserParam = {
                    AccessToken: tokens.access_token
                } as AWS.CognitoIdentityServiceProvider.Types.GetUserRequest
                return Promise.all([Promise.resolve(tokens), cognito.getUser(getUserParam).promise()]);
            })
            .then((results: any[]) => {
                const tokens = results[0];
                const cognitoUserResponse: AWS.CognitoIdentityServiceProvider.Types.GetUserResponse = results[1];
                const usernameAttribute = cognitoUserResponse.UserAttributes
                    .find((userAttribute: AWS.CognitoIdentityServiceProvider.Types.AttributeType) => {
                        return userAttribute.Name === 'email';
                    });
                if (!usernameAttribute) {
                    throw new Error('UserLoginService: Cognito response does not contain the email attribute. Please ensure SAML attribute mapping is set');
                }
                const username = usernameAttribute.Value;
                const sessionData = {
                    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
                    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
                    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token })
                } as ICognitoUserSessionData;
                const session = new CognitoUserSession(sessionData);

                const userData = {
                    Username: username,
                    Pool: pool,
                    Storage: window.sessionStorage,
                } as ICognitoUserData;

                const cognitoUser = new CognitoUser(userData);
                cognitoUser.setSignInUserSession(session);
                // Do a logout to trigger a Global Logout and invalidate tokens in other sessions (ie other browsers)
                const crossSessionGlobalLogoutPerformed = window.sessionStorage.getItem('crossSessionGlobalLogoutPerformed');
                if (!crossSessionGlobalLogoutPerformed) {
                    this.globalLogout()
                        .then(() => {
                            window.sessionStorage.setItem('crossSessionGlobalLogoutPerformed', 'true');
                            this.document.location.href = this.document.location.href.substring(0, this.document.location.href.indexOf('?code='));
                        });
                } else {
                    this.document.location.href = this.document.location.href.substring(0, this.document.location.href.indexOf('?code='));
                }
                // this.cacheTokens(username, tokens.id_token, tokens.access_token, tokens.refresh_token);
                // this.router.navigate(['/login']);
                // this.router.navigate(['/']);
                return;
            })
    }



    // cacheTokens(username: string, idToken: string, accessToken: string, refreshToken: string) {
    //     const pool = this.cognitoUtil.getUserPool();
    //     const keyPrefix = `CognitoIdentityServiceProvider.${pool.getClientId()}`;
    //     const idTokenKey = `${keyPrefix}.${username}.idToken`;
    //     const accessTokenKey = `${keyPrefix}.${username}.accessToken`;
    //     const refreshTokenKey = `${keyPrefix}.${username}.refreshToken`;
    //     const lastUserKey = `${keyPrefix}.LastAuthUser`;

    //     window.localStorage.setItem(idTokenKey, idToken);
    //     window.localStorage.setItem(accessTokenKey, accessToken);
    //     window.localStorage.setItem(refreshTokenKey, refreshToken);
    //     window.localStorage.setItem(lastUserKey, username);
    // }
}
